import { useMemo } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useUnit } from "components/Isometry/Isometry.utilities";
import { Icon } from "components/Icons";
import { useActiveUnit } from "components/Unit";
import { getProjectData } from "state/jotai.projectDataHooks";
import Unit from "components/Unit/Unit.model";

/* ================= CONFIG ================= */
const POPUP_WIDTH = 300;
const POPUP_HEIGHT = 200;
const OFFSET = 12;

/* ================= STYLES ================= */
const HoverBox = styled.div`
  position: fixed;
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  transform: translate(12px, 12px);
  width: 300px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid var(--Travbyen-Secondary, #f8f9f1);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 9999;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  background: #f8f9f1;
  border-radius: 16px 16px 0 0;
`;

const Title = styled.h3`
  margin: 0;
  color: ${({ clr }) => clr?.primary};
`;

const Status = styled.span`
  padding: 4px 8px;
  border-radius: 999px;
  color: #fff;
  background: ${({ statusColor }) => statusColor};
`;

const Body = styled.div`
  padding: 0 0 8px 16px;
`;

const Price = styled.div`
  margin: 8px 0;
  color: ${({ clr }) => clr?.primary};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  row-gap: 8px;
  color: ${({ clr }) => clr?.primary};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* ================= HELPERS ================= */

const formatMoveInDate = (date) =>
  date ? date.replace(/-/g, ".") : "";

const getSmartPosition = (pos) => {
  if (!pos) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let { x, y } = pos;

  if (x + POPUP_WIDTH + OFFSET > vw) {
    x -= POPUP_WIDTH + OFFSET;
  }

  if (y + POPUP_HEIGHT + OFFSET > vh) {
    y -= POPUP_HEIGHT + OFFSET;
  }

  return { x, y };
};

/* ================= COMPONENT ================= */

export const InnerDesktopLabel = ({ hoveredId, hoveredPos, clr }) => {
  const units = getProjectData("units", "Units");
  const activeUnit = useActiveUnit();
  const getUnitValue = useUnit();

  /** 🔹 Resolve current unit */
  const currentUnit = useMemo(() => {
    if (!units?.length) return null;

    if (hoveredId) {
      const id = getUnitValue(hoveredId, "UnitID");
      if (id) return units.find((u) => +u.UnitID === +id);
    }

    const activeId = activeUnit?.getId?.();
    return units.find((u) => +u.UnitID === +activeId);
  }, [units, hoveredId, activeUnit, getUnitValue]);

  const cUnit = useMemo(() => new Unit(currentUnit), [currentUnit]);

  /** 🔹 Derived values */
  const data = currentUnit?.Data || {};

  const isRent = cUnit?.isRent?.();
  const price = cUnit?.getPrice?.();
  const status = data?.UnitStatusName?.Value || "Ledig";
  const statusColor = cUnit?.getValue?.("UnitColor");
const availablePris = cUnit?.data?.Data?.Pris_Tekst_DK?.Value === "";
  const moveInDate = formatMoveInDate(
    data?.["DATE_FORMAT(Ledig_pr, '%d-%m-%Y')"]?.Value
  );

  const visible = currentUnit && hoveredPos;
  const smartPos = useMemo(
    () => getSmartPosition(hoveredPos),
    [hoveredPos]
  );

  if (!visible || !smartPos) return null;

  return createPortal(
    <HoverBox x={smartPos.x} y={smartPos.y}>
      <Header>
        <Title className="caption-medium" clr={clr}>
          {data?.Adresse?.Value}
        </Title>

        <Status className="caption-medium" statusColor={statusColor}>
          {status}
        </Status>
      </Header>

      <Body>
        {availablePris ? (
          <Price className="h5" clr={clr}>
            {price}
            {isRent ? "/md" : ""}
          </Price>
        ) : null}

        <List clr={clr}>
          <Item className="paragraph2-regular">
            <Icon isFaIcon icon="house" sx={{ width: 24, height: 24 }} style={{fontSize:"16px"}}/>
           {data?.SalesTypeName?.Value?.toLowerCase()}
          </Item>

          <Item className="paragraph2-regular">
            <Icon icon="area" sx={{ width: 16, height: 16 }} />
            {data?.Areal?.Value} m²
          </Item>

          <Item className="paragraph2-regular">
            <Icon icon="rooms" sx={{ width: 16, height: 16 }} />
            {data?.VaerelserAntal?.Value} værelser
          </Item>

         {
              moveInDate && <Item className="paragraph2-regular">
                <Icon isFaIcon icon="key" style={{ fontSize: '16px' }} />
                Indflytning: {moveInDate}
              </Item>
            }
        </List>
      </Body>
    </HoverBox>,
    document.body
  );
};